import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Sparkles, Zap, Bug, Loader2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { useChangelog } from '@/hooks/useChangelog'
import { groupByLocalDay, localizedText, nextLastSeenAt } from '@/lib/changelog'
import { env } from '@/lib/env'
import type { ChangelogEntry, ChangelogEntryType } from '@/services/changelog-service'

interface ChangelogModalProps {
  open: boolean
  onClose: () => void
  isStartupMode?: boolean
}

const TYPE_STYLE: Record<ChangelogEntryType, { Icon: LucideIcon; className: string }> = {
  feature: { Icon: Sparkles, className: 'bg-purple-500/15 text-purple-600' },
  improvement: { Icon: Zap, className: 'bg-amber-500/15 text-amber-600' },
  fix: { Icon: Bug, className: 'bg-red-500/15 text-red-600' },
}

function ChangelogEntryItem({ entry, lang }: { entry: ChangelogEntry; lang: string }) {
  const { t } = useTranslation()
  const { Icon, className } = TYPE_STYLE[entry.type]
  const howTo = localizedText(entry.howTo, lang)
  return (
    <li className="space-y-1.5" data-testid="changelog-entry">
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {t(`changelog.types.${entry.type}`)}
        </span>
        <h4 className="text-sm font-semibold text-[var(--text-color)]">
          {localizedText(entry.title, lang)}
        </h4>
      </div>
      <p className="whitespace-pre-line text-sm text-[var(--text-secondary)]">
        {localizedText(entry.body, lang)}
      </p>
      {howTo && (
        <div className="rounded-lg border border-[var(--window-border-color)] px-3 py-2 text-sm text-[var(--text-secondary)]">
          <span className="font-medium text-[var(--text-color)]">{t('changelog.howTo')}: </span>
          <span className="whitespace-pre-line">{howTo}</span>
        </div>
      )}
    </li>
  )
}

export function ChangelogModal({ open, onClose, isStartupMode = false }: ChangelogModalProps) {
  const { t, i18n } = useTranslation()
  const { settings, saveSettings } = useSettingsContext()
  const modalRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [hideOnStartup, setHideOnStartup] = useState(
    () => settings.changelog?.hideOnStartup ?? false
  )
  const {
    entries,
    isPending,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useChangelog(open)

  // The modal stays mounted while closed, so the settings may have loaded
  // after the first render; take the stored choice at every opening.
  const storedHideOnStartup = settings.changelog?.hideOnStartup ?? false
  useEffect(() => {
    if (open) setHideOnStartup(storedHideOnStartup)
  }, [open, storedHideOnStartup])

  const lang = i18n.language
  const days = groupByLocalDay(entries)

  const handleClose = () => {
    saveSettings({
      ...settings,
      changelog: {
        ...settings.changelog,
        lastSeenAt: nextLastSeenAt(settings.changelog?.lastSeenAt, entries[0]?.publishedAt),
        hideOnStartup,
      },
    })
    onClose()
  }
  // The listeners below are attached once per opening; the ref lets them
  // close with the current checkbox state and entries.
  const handleCloseRef = useRef(handleClose)
  handleCloseRef.current = handleClose

  // ESC to close (only in non-startup mode)
  useEffect(() => {
    if (!open || isStartupMode) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseRef.current()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, isStartupMode])

  // Click outside to close (only in non-startup mode)
  useEffect(() => {
    if (!open || isStartupMode) return

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        closeTimeoutRef.current = setTimeout(() => handleCloseRef.current(), 100)
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, isStartupMode])

  if (!open) return null

  const dayFormat = new Intl.DateTimeFormat(lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className="fixed inset-0 z-[15000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={modalRef}
        role="dialog"
        aria-labelledby="changelog-title"
        className="relative w-full max-w-3xl h-[85vh] bg-[var(--window-background)] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-[var(--window-border-color)]"
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-[var(--window-border-color)]">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="changelog-title" className="text-xl font-semibold text-[var(--text-color)]">
                {t('changelog.title')}
              </h2>
              <p className="text-sm text-[var(--text-secondary)]" data-testid="changelog-version">
                {t('changelog.version')}: {env.appVersion}
              </p>
            </div>
          </div>
          {!isStartupMode && (
            <button
              onClick={handleClose}
              aria-label={t('common.close')}
              className="p-2 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {isPending && (
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]" role="status">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('changelog.loading')}
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-start gap-3" role="alert">
              <p className="text-sm text-[var(--text-secondary)]">{t('changelog.loadError')}</p>
              <button
                onClick={() => void refetch()}
                className="rounded-lg border border-[var(--window-border-color)] px-3 py-1.5 text-sm text-[var(--text-color)] hover:bg-[var(--sidebar-hover)]"
              >
                {t('common.retry')}
              </button>
            </div>
          )}

          {!isPending && !isError && days.length === 0 && (
            <p className="text-sm text-[var(--text-secondary)]">{t('changelog.empty')}</p>
          )}

          {days.map((day, index) => (
            <section
              key={day.day}
              data-testid="changelog-day"
              className={index > 0 ? 'pt-6 border-t border-[var(--window-border-color)]' : ''}
            >
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-color)]">
                  {dayFormat.format(day.date)}
                </h3>
                {index === 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/15 text-green-600">
                    {t('changelog.latest')}
                  </span>
                )}
              </div>
              <ul className="space-y-4">
                {day.entries.map((entry) => (
                  <ChangelogEntryItem key={entry.key} entry={entry} lang={lang} />
                ))}
              </ul>
            </section>
          ))}

          {hasNextPage && (
            <div className="flex flex-col items-center gap-2 pt-2">
              {isFetchNextPageError && (
                <p className="text-sm text-[var(--text-secondary)]" role="alert">
                  {t('changelog.loadMoreError')}
                </p>
              )}
              <button
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid="changelog-load-more"
                className="rounded-lg border border-[var(--window-border-color)] px-4 py-1.5 text-sm text-[var(--text-color)] hover:bg-[var(--sidebar-hover)] disabled:opacity-60"
              >
                {isFetchingNextPage ? t('changelog.loadingMore') : t('changelog.loadMore')}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[var(--window-border-color)] bg-[var(--window-titlebar-bg)]">
          <div className="flex items-center justify-between gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideOnStartup}
                onChange={(e) => setHideOnStartup(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-[var(--text-secondary)]">
                {t('changelog.dontShowAgain')}
              </span>
            </label>
            {isStartupMode && (
              <button
                onClick={handleClose}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg"
              >
                {t('changelog.confirm')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
